import { useMemo, useState } from 'react'
import { Sigma, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formulaToHuman } from '@/lib/formula'
import {
  OperandInput,
  emptyOperand,
  serializeOperand,
  type ConditionOperand,
  type OperandState,
} from './ConditionBuilder'

interface Props {
  /** Which aggregate function this instance builds. */
  fn: 'SUM' | 'MAX'
  /** Fields (or library placeholders) offered in the operand pickers. */
  operands: ConditionOperand[]
  /** Library mode: allow typing a brand-new placeholder name in any slot. */
  allowCustomRefs?: boolean
  /** Called with the complete, valid SUM(...)/MAX(...) expression. */
  onInsert: (formula: string) => void
  onCancel: () => void
  className?: string
}

/**
 * Structured SUM(...)/MAX(...) editor — mirrors ConditionBuilder's approach
 * of only emitting the formula once every slot is filled, so a half-built
 * aggregate never pollutes the stored formula.
 */
export function AggregateBuilder({
  fn,
  operands,
  allowCustomRefs,
  onInsert,
  onCancel,
  className,
}: Props) {
  const [args, setArgs] = useState<OperandState[]>([emptyOperand(), emptyOperand()])

  const patchArg = (idx: number, o: OperandState) =>
    setArgs(prev => prev.map((a, i) => (i === idx ? o : a)))
  const addArg = () => setArgs(prev => [...prev, emptyOperand()])
  const removeArg = (idx: number) => setArgs(prev => prev.filter((_, i) => i !== idx))

  const serialized = useMemo(() => {
    const parts = args.map(serializeOperand)
    if (parts.length === 0 || parts.some(p => !p)) return null
    return `${fn}(${parts.join(', ')})`
  }, [args, fn])

  const preview = useMemo(() => {
    if (!serialized) return null
    const labels: Record<string, string> = {}
    for (const o of operands) labels[o.id] = o.label
    // New placeholders aren't in the operand list — label them with their own name.
    for (const m of serialized.matchAll(/\{([^}]+)\}/g)) {
      if (!(m[1] in labels)) labels[m[1]] = m[1]
    }
    return formulaToHuman(serialized, labels)
  }, [serialized, operands])

  return (
    <div className={cn('space-y-2 rounded-md border bg-card p-2.5', className)}>
      <div className='flex items-center gap-1.5'>
        <Sigma className='h-3.5 w-3.5 text-muted-foreground' />
        <span className='text-xs font-semibold'>
          Build {fn === 'SUM' ? 'SUM' : 'MAX'} expression
        </span>
      </div>

      <div className='space-y-1.5'>
        {args.map((a, i) => (
          <div key={i} className='flex items-start gap-1'>
            <OperandInput
              value={a}
              onChange={o => patchArg(i, o)}
              operands={operands}
              allowCustomRefs={allowCustomRefs}
              placeholder={`Value ${i + 1}…`}
            />
            {args.length > 1 && (
              <Button
                type='button'
                size='icon'
                variant='ghost'
                className='h-8 w-6 shrink-0 text-muted-foreground'
                title='Remove value'
                onClick={() => removeArg(i)}
              >
                <X className='h-3.5 w-3.5' />
              </Button>
            )}
          </div>
        ))}
        <Button
          type='button'
          size='sm'
          variant='ghost'
          className='h-6 px-1.5 text-[11px] text-muted-foreground'
          onClick={addArg}
        >
          <Plus className='mr-1 h-3 w-3' /> Add value
        </Button>
      </div>

      {preview ? (
        <div className='rounded bg-muted/40 px-2 py-1.5'>
          <p className='text-[10px] uppercase tracking-wide text-muted-foreground'>Preview</p>
          <p className='text-xs font-medium break-words'>{preview}</p>
        </div>
      ) : (
        <p className='text-[11px] text-muted-foreground'>Fill every value to enable Insert.</p>
      )}

      <div className='flex items-center justify-end gap-2'>
        <Button type='button' size='sm' variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='button'
          size='sm'
          disabled={!serialized}
          onClick={() => serialized && onInsert(serialized)}
        >
          Insert {fn === 'SUM' ? 'sum' : 'max'}
        </Button>
      </div>
    </div>
  )
}
